import { BreakpointObserver } from '@angular/cdk/layout';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, combineLatest, from, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FeeService } from '../../credit-account/fee.service';
import {
    isEditPage,
    isRelistPage,
    isAgentPage
} from '../../helpers/router.helper';
import { StepIds } from '../../models/property-step/property-step-model';
import { PROPERTY_UI_COMPACT_MAX_WIDTH } from '../../property-ui-constants';
import {
    DraftFacade,
    IRouterData,
    RouterFacade,
    StepInfo
} from '@classifieds-sell/state';
import { FeeCalculationFacade } from '../../state/fee-calculation/fee-calculation.facade';
import { UserFacade } from '@classifieds-sell/state';
import { DelayService, WindowRef } from '@trademe/tangram';
import { ClearCreditCardRedirectResponseDataParam, ExtractCreditCardRedirectResponseData } from 'libs/trademe-core-ui/src/lib/ping/ping-widget/credit-card-redirect-helper';
import { ValidationResult } from '@classifieds-sell/datalayer';

@Component({
    selector: 'property-page-footer',
    templateUrl: './property-page-footer.component.html',
    styleUrls: ['./property-page-footer.component.scss'],
    standalone: false
})
export class PropertyPageFooterComponent implements OnInit, OnDestroy, AfterViewInit {
    private _destroyed$: Subject<void> = new Subject<void>();

    @Output() continue = new EventEmitter<StepInfo>();
    @Output() saveAndCompleteLater = new EventEmitter<void>();
    @Output() createListing = new EventEmitter<void>();
    @Output() topUpCompleted = new EventEmitter();
    @Output() editListing = new EventEmitter<void>();
    @Output() relistListing = new EventEmitter<void>();

    routerData: IRouterData;

    isLoading = false;
    isLoadingEmitter$ = new BehaviorSubject<boolean>(this.isLoading);
    listingActionLoading$ = this.draftFacade.listingActionLoading$;
    listingActionResponse$ = this.draftFacade.listingActionResponse$;
    createOrEditOrRelistListingEnabled: boolean;
    isCreditAccountModalVisible = false;

    continueToTitle$ = new BehaviorSubject<string>('');

    isCompact: boolean;
    shouldTopUp = true;

    isAgent = false;
    isEditOrRelistPage = false;
    isUpgradePackage = false;
    isEnoughBalance = false;
    isIncreasePricingTier = false;
    isEditPage = false;
    isRelistPage = false;

    get isSummaryStep() {
        return this.routerData?.stepInfo?.stepId === StepIds.propertySummary;
    }

    constructor(
        private routerFacade: RouterFacade,
        private draftFacade: DraftFacade,
        private breakpointObserver: BreakpointObserver,
        private changeDetectorRef: ChangeDetectorRef,
        private feeService: FeeService,
        private route: ActivatedRoute,
        private userFacade: UserFacade,
        private feeCalculationFacade: FeeCalculationFacade,
        private windowRef: WindowRef,
        private delayService: DelayService
    ) {}

    ngAfterViewInit(): void {
        const psd = ExtractCreditCardRedirectResponseData(this.windowRef);
        if (psd) {
            this.windowRef.useWindow(window => {
                this.delayService.createDelay(1000).subscribe(() => {
                    window.scrollTo(0, window.document.body.scrollHeight);
                    this.isCreditAccountModalVisible = true;
                    this.changeDetectorRef.detectChanges();
                });
            });
        }
    }

    ngOnDestroy(): void {
        this._destroyed$.next();
    }

    ngOnInit(): void {
        this.isAgent = isAgentPage(this.route, true);
        this.isRelistPage = isRelistPage(this.route, true);
        this.isEditPage = isEditPage(this.route, true);
        this.isEditOrRelistPage = this.isEditPage || this.isRelistPage;
        if (this.isEditOrRelistPage && !this.isAgent) {
            this.setIsUpgradePackage();
            this.setIsEnoughBalance();
            this.setIsIncreasePricingTier();
            const validation: ValidationResult = {
                stepId: StepIds.propertySummary,
                isValid: true
            };

            this.draftFacade.updateValidationResults(validation);
        }

        from(this.routerFacade.routerData$)
            .pipe(takeUntil(this._destroyed$))
            .subscribe((routerStateData) => {
                this.routerStateSubscriber(routerStateData);
            });

        combineLatest([
            this.routerFacade.routerData$,
            this.draftFacade.validationResults$,
            this.feeService.isTopUpRequired$
        ])
            .pipe(takeUntil(this._destroyed$))
            .subscribe(
                ([routerStateData, validationResults, isTopUpRequired]) => {
                    this.routerStateSubscriber(routerStateData);

                    if (this.isSummaryStep) {
                        this.shouldTopUp = isTopUpRequired;
                        const filteredValidationResults =
                            validationResults?.filter(
                                (v) => v.stepId !== StepIds.propertySummary
                            ) || [];
                        this.createOrEditOrRelistListingEnabled =
                            filteredValidationResults.length > 0 &&
                            filteredValidationResults.every((v) => v.isValid) &&
                            !this.shouldTopUp;
                    }
                }
            );

        this.breakpointObserver
            .observe([PROPERTY_UI_COMPACT_MAX_WIDTH])
            .subscribe((result) => {
                this.isSmallChange(result.matches);
            });
    }

    private setIsEnoughBalance(): void {
        combineLatest([
            this.userFacade.memberSummary$,
            this.feeCalculationFacade.totalFee$
        ])
            .pipe(takeUntil(this._destroyed$))
            .subscribe(([memberSummary, totalFee]) => {
                const memberBalance = memberSummary?.balance ?? 0;
                this.isEnoughBalance = memberBalance >= totalFee;
            });
    }

    private setIsIncreasePricingTier() {
        from(this.feeCalculationFacade.isIncreasePricingTier$)
            .pipe(takeUntil(this._destroyed$))
            .subscribe((isIncreasePricingTier) => {
                this.isIncreasePricingTier = isIncreasePricingTier;
            });
    }

    private setIsUpgradePackage(): void {
        from(this.draftFacade.state$)
            .pipe(takeUntil(this._destroyed$))
            .subscribe((state) => {
                if(isRelistPage) {
                    // relist defaults to bronze, take this over the originalDraftData
                    const bronze = state.metadata.fees.slice().sort((a, b) => a.id - b.id)[0]?.id;
                    this.isUpgradePackage =
                    state.draftData.propertyPromote.categoryPackageId > bronze;
                } else {
                    this.isUpgradePackage =
                    state.draftData.propertyPromote.categoryPackageId >
                    state.originalDraftData.propertyPromote.categoryPackageId;
                }
            });
    }

    private isSmallChange(isSmall: boolean) {
        this.isCompact = isSmall;
        this.changeDetectorRef.markForCheck();
    }

    private routerStateSubscriber(routerStateData: IRouterData): void {
        this.routerData = routerStateData;

        if (routerStateData && this.routerData.stepInfo) {
            if (routerStateData.stepInfo.nextStep) {
                this.continueToTitle$.next(
                    routerStateData.stepInfo.nextStep.title.toLocaleLowerCase()
                );
            }
        }
    }

    onContinueTo() {
        this.continue.emit(this.routerData.stepInfo);
    }

    onSaveAndCompleteLater() {
        this.saveAndCompleteLater.emit();
    }

    onCreateListing() {
        this.createListing.emit();
    }

    onEditListing() {
        this.editListing.emit();
    }

    onRelistListing() {
        this.relistListing.emit();
    }

    onEditOrRelistListing() {
        if (this.isEditPage) {
            this.onEditListing();
        } else if (this.isRelistPage) {
            this.onRelistListing();
        }
    }

    onShowTopUpModal(showTopUpModal: boolean): void {
        this.isCreditAccountModalVisible = showTopUpModal;
    }

    onCreditAdded(): void {
        this.onShowTopUpModal(false);
        this.topUpCompleted.emit();
        ClearCreditCardRedirectResponseDataParam(this.windowRef);
    }

    onCreditFailure(): void {
        this.onShowTopUpModal(false);
    }
}
